import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { SaveStatus } from './SaveStatus';
import { RemindMeButton } from './RemindMeButton';
import { DismissBackdrop } from './DismissBackdrop';

// The right-hand side of a page header (#265, #269): saved time, an
// always-visible slot, then the page's actions. It must sit inside an
// element with the `@container` class, and steps down by that width:
//   < 640px  action labels hide, leaving icons
//   < 460px  actions fold into a "..." menu
// The save status and the `always` slot never hide.

export type HeaderAction =
  | {
      kind?: 'button';
      key: string;
      icon: ReactNode;
      label: string;
      onClick: () => void;
      danger?: boolean;
      /** Show only the icon at every width (e.g. Export, Print). */
      iconOnly?: boolean;
    }
  | { kind: 'remind'; key: string; entityType: string; entityId: string; entityTitle: string };

interface Props {
  /** `undefined` = this page doesn't track a saved time; `null` = not saved yet. */
  lastSaved?: string | null;
  saving?: boolean;
  /** Always visible, before the actions (e.g. a view toggle). */
  always?: ReactNode;
  actions: HeaderAction[];
}

export function HeaderActions({ lastSaved, saving, always, actions }: Props) {
  return (
    <div className="flex items-center gap-0.5 flex-shrink-0">
      {/* No reserved width (#271); saving only recolours the icon (#275). */}
      <SaveStatus lastSaved={lastSaved} saving={saving} className="mr-1" />
      {always}
      {actions.length > 0 && (
        <>
          <div className="flex items-center @max-[460px]:hidden" data-testid="header-actions">
            {actions.map(a => <InlineAction key={a.key} action={a} />)}
          </div>
          <MoreMenu actions={actions} />
        </>
      )}
    </div>
  );
}

function InlineAction({ action: a }: { action: HeaderAction }) {
  if (a.kind === 'remind') {
    return <RemindMeButton entityType={a.entityType} entityId={a.entityId} entityTitle={a.entityTitle} labelClassName="@max-[640px]:hidden" />;
  }
  const tone = a.danger ? 'hover:text-danger' : 'hover:text-text-primary';
  if (a.iconOnly) {
    return (
      <button type="button" onClick={a.onClick} title={a.label} aria-label={a.label}
        className={`p-1.5 rounded-md text-text-muted ${tone} hover:bg-bg-hover transition-colors`}>
        {a.icon}
      </button>
    );
  }
  return (
    <button type="button" onClick={a.onClick} title={a.label}
      className={`flex items-center gap-1 px-2 @max-[640px]:px-1.5 py-1 text-xs text-text-muted ${tone} hover:bg-bg-hover rounded transition-colors whitespace-nowrap`}>
      {a.icon}
      <span className="@max-[640px]:hidden">{a.label}</span>
    </button>
  );
}

/** Narrowest step only: every action in one popover. */
function MoreMenu({ actions }: { actions: HeaderAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const item = 'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary text-left transition-colors';
  return (
    <div className={`relative hidden @max-[460px]:block${open ? ' z-30' : ''}`} ref={ref} data-testid="header-more">
      {/* #121: a tap outside only closes the menu. The backdrop sits inside the
          (raised) container so it shares the container's hidden breakpoint. */}
      {open && <DismissBackdrop onDismiss={() => setOpen(false)} />}
      <button type="button" onClick={() => setOpen(o => !o)} title="More actions" aria-label="More actions"
        aria-expanded={open} className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors">
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-30 bg-bg-surface border border-border rounded-lg shadow-xl py-1 w-48" role="menu">
          {actions.map(a => a.kind === 'remind' ? (
            <div key={a.key} className="px-1">
              <RemindMeButton entityType={a.entityType} entityId={a.entityId} entityTitle={a.entityTitle} />
            </div>
          ) : (
            <button key={a.key} type="button" role="menuitem"
              className={`${item} ${a.danger ? 'hover:text-danger' : ''}`}
              onClick={() => { setOpen(false); a.onClick(); }}>
              {a.icon} {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
